import {
  ColumnVisibilityDropdown,
  VisibilityState,
  visibilityStateUrlSerializer,
} from "@/components/ColumnVisibilityToggle";
import { DataTable } from "@/components/DataTable";
import { IndeterminateCheckbox } from "@/components/IndeterminateCheckbox";
import { getSosEntityLineItemHashId } from "@/components/SosEntityLineItemHashLink";
import { TableWrapper } from "@/components/TableWrapper";
import { useColumnSetVisibility } from "@/hooks/useColumnSetVisibility";
import { PersistedStateActionButtons, usePersistedState } from "@/hooks/usePersistedState";
import { withJobTrackingData } from "../withJobTrackingData";
import { purchaseOrdersTableColumns, PurchaseOrderTableRow } from "./purchase-orders-table-columns";

const pricingDetailColumnIds = new Set([
  "po_currency_code",
  "po_exchangeRate",
  "po_jobLinesSummary_categorizedCosts_material_subtotal",
  "po_jobLinesSummary_categorizedCosts_nonMaterial_subtotal",
  "po_jobLinesSummary_categorizedCosts_shipping_subtotal",
  "line_totalCost_tax",
  "line_totalCost_total",
]);

export const PurchaseOrders = withJobTrackingData(({ purchaseOrders }) => {
  // Persisted column visibility state
  const [columnVisibility, setColumnVisibility, persistedStateActions] = usePersistedState<VisibilityState>({
    componentId: "purchase-orders-hidden-cols",
    urlKey: "hidden-cols",
    stateSchema: VisibilityState,
    version: 1,
    defaultState: Object.fromEntries([...pricingDetailColumnIds].map((id) => [id, false])),
    urlSerializer: visibilityStateUrlSerializer,
  });

  // Use hook for toggling visibility on sets of columns
  const [showPricingDetails, toggleShowPricingDetails] = useColumnSetVisibility(
    pricingDetailColumnIds,
    columnVisibility,
    setColumnVisibility,
  );

  // Flatten the list of item receipts into a list of table rows (one row for each line item and receiving cost)
  const data = purchaseOrders.flatMap((po) =>
    po.jobLines.map<PurchaseOrderTableRow>((line, index) => ({
      rowId: getSosEntityLineItemHashId("purchaseorder", po.id, index),
      po,
      line,
    })),
  );

  return (
    <>
      <div className="col-auto mb-3">
        <div className="row row-cols-auto gx-3 align-items-center">
          <div>
            <IndeterminateCheckbox
              id="filter-pricing-details"
              label="Show Detailed Pricing"
              checked={showPricingDetails}
              onChange={toggleShowPricingDetails}
            />
          </div>
          <ColumnVisibilityDropdown
            columns={purchaseOrdersTableColumns}
            columnVisibility={columnVisibility}
            onColumnVisibilityChange={setColumnVisibility}
          />
          <PersistedStateActionButtons {...persistedStateActions} />
        </div>
      </div>
      <TableWrapper className="col">
        <DataTable
          bordered
          hover
          data={data}
          columns={purchaseOrdersTableColumns}
          getRowId={({ rowId }) => rowId}
          state={{ columnVisibility }}
          initialState={{
            sorting: [{ id: "po_date", desc: true }],
          }}
        />
      </TableWrapper>
    </>
  );
});
